import React, { forwardRef, useCallback, useContext, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { message } from 'antd';
import { WidgetType } from '@kemu-io/kemu-core/types';
import { HelpServicePanelRef } from '../types';
import { clerkApiInterceptor } from '../../../api/authenticatedApi/clerkApiInterceptor';
import styles from './NativeWidgetHelpContent.module.css';
import MarkdownViewer from '@components/MarkdownViewer/MarkdownViewer';
import { useTranslation } from '@hooks/index';
import { PLATFORM_DOCS_RELATIVE_SUFFIX, PublicationDocsRelativePathPrefix, SELF_NAVIGATION_SUFFIX } from '@common/constants';
import { getNativeWidgetPublicUrl, getPlatformDocsPublicUrl } from '@common/utils';
import { ApplicationContext } from '@common/context/ApplicationContext/ApplicationContext';
import globals from '@common/globals';

type Props = {
  type: WidgetType;
  recipeId: string;
  thingRecipeId: string;
  widgetId: string;
  onDraggingKemuSnippet: (isDragging: boolean) => void;
  onLoadingContent: (isLoading: boolean) => void;
  onShowBackButton: (show: boolean) => void;
  isPlatformHelp?: boolean;
}

const relativePathPrefix = PublicationDocsRelativePathPrefix;

const NativeWidgetHelpContent = forwardRef<HelpServicePanelRef, Props>((props, ref) => {
  const { type, onDraggingKemuSnippet, onLoadingContent, onShowBackButton, isPlatformHelp } = props;
  const docsUrlRef = useRef<string | null>(null);
  const { language } = useContext(ApplicationContext);
  const contentHistoryRef = useRef<string[]>([]);
  const contentContainerRef = useRef<HTMLDivElement>(null);
  const t = useTranslation('NativeWidgetHelpContent');
  const [currentContent, setCurrentContent] = useState<string | null>(null);

  // Platform docs paths for reuse
  const platformDocsPath = `/docs/platform/${globals.WEB_APP_VERSION}/${language}`;
  const platformDocsUrl = `platform${platformDocsPath}`;

  useImperativeHandle(ref, () => ({
    /**
     * Pops the last content from the history and sets it as the current content
     */
    popContentHistory: () => {
      contentHistoryRef.current.pop();
      setCurrentContent(contentHistoryRef.current[contentHistoryRef.current.length - 1]);
    },
    renderCustomContent: (content: string) => {
      contentHistoryRef.current = [content];
      setCurrentContent(content);
    },
  }), []);

  const setHelpContent = useCallback((md: string, docsUrl: string) => {
    docsUrlRef.current = docsUrl;
    contentHistoryRef.current.push(md);
    setCurrentContent(md);
  }, []);

  const setError = useCallback((title: string, error: string) => {
    setCurrentContent(`<h2>${title} 😨</h2><p style="color: red;">${error}</p>`);
  }, []);

  /** Generic function for fetching a resource from the widget's docs folder */
  const fetchResourceForWidget = useCallback(async (resourcePath: string) => {
    const url = `${getNativeWidgetPublicUrl(type, resourcePath)}`;
    const response = await fetch(url);

    if (!response.ok) {
      setError(t('FetchError'), response.statusText);
      return null;
    }

    const text = await response.text();
    return text;
  }, [setError, t, type]);


  const fetchNestedLink = useCallback(async (resourcePath: string) => {
    // Determine if this is a platform docs link or widget docs link
    const isPlatformDocsLink = resourcePath.includes('/platform/');

    if (isPlatformDocsLink) {
      // For platform docs, use authenticated API
      try {
        const apiPath = resourcePath.replace('/docs/platform/', '/v1/platform/docs/');
        const response = await clerkApiInterceptor.get(globals.PLATFORM_API_NAME, apiPath, { response: true });
        return response.data.content;
      } catch (error) {
        console.error('Platform docs API error:', error);
        setError(t('FetchError'), error.message || 'Failed to fetch platform documentation');
        return null;
      }
    } else {
      // For widget docs, use direct S3 access
      const url = `${getPlatformDocsPublicUrl(resourcePath)}`;
      const response = await fetch(url);

      if (!response.ok) {
        setError(t('FetchError'), response.statusText);
        return null;
      }

      const text = await response.text();
      return text;
    }
  }, [setError, t]);


  const fetchHelp = useCallback(async () => {
    onLoadingContent(true);
    const help = isPlatformHelp
      ? await fetchNestedLink(`${platformDocsPath}/help.md`)
      : await fetchResourceForWidget(`/docs/widgets/${type}/panel-${language}.md`);

      onLoadingContent(false);
    if (!help) {
      return;
    }
    // Seed history with the initial content so one navigation enables back
    contentHistoryRef.current = [];
    setHelpContent(help, '');
  }, [language, onLoadingContent, type, isPlatformHelp, platformDocsPath, fetchNestedLink, fetchResourceForWidget, setHelpContent]);



  /**
   * Intercept link clicks and open ":self:" links within this panel
   */
  const handleLinkClick = useCallback((event: React.MouseEvent<HTMLAnchorElement>, rawUrl: string | undefined) => {

    // Handle anchor links (internal navigation within the same document)
    if (rawUrl?.startsWith('#')) {
      event.preventDefault();
      const targetId = rawUrl.substring(1);

      // Search within the content container to avoid conflicts with other page elements
      if (contentContainerRef.current) {
        const targetElement = contentContainerRef.current?.querySelector(`[id="${targetId}"]`);

        if (targetElement) {
          targetElement.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
          });
          return;
        } else {
          // Show error message if the anchor was not found
          message.config({
            getContainer: () => contentContainerRef.current as HTMLElement,
          });
          message.error(t('AnchorNotFoundWithName', { sectionName: targetId }));
          return;
        }
      }
    }

    // Open links relative to the widget's docs folder
    if (rawUrl?.startsWith(relativePathPrefix) && rawUrl?.endsWith(SELF_NAVIGATION_SUFFIX)) {
      const url = rawUrl.replace(SELF_NAVIGATION_SUFFIX, '');
      const fetchNow = async () => {
        onLoadingContent(true);
        const text = await fetchResourceForWidget(url);
        setHelpContent(text || '', '');
        onLoadingContent(false);
      };

      event.preventDefault();
      fetchNow();
    }

    // Open links relative to the platform docs folder
    if (rawUrl?.startsWith(relativePathPrefix) && rawUrl?.endsWith(PLATFORM_DOCS_RELATIVE_SUFFIX)) {
      const url = rawUrl.replace(PLATFORM_DOCS_RELATIVE_SUFFIX, '');
      // For platform docs, construct the full path with version and language
      // Remove the leading /docs/ and add the full platform path
      const filename = url.replace('/docs/', '');
      const fullPlatformPath = `${platformDocsPath}/${filename}`;

      // Determine if this is a native widget docs link
      const isNativeWidgetDocsLink = url.includes('/widgets/');
      const fetchNow = async () => {
        onLoadingContent(true);
        const text = await fetchNestedLink(isNativeWidgetDocsLink ? url : fullPlatformPath);
        setHelpContent(text || '', '');
        onLoadingContent(false);
      };

      event.preventDefault();
      fetchNow();
    }
  }, [fetchResourceForWidget, fetchNestedLink, setHelpContent, onLoadingContent, t, platformDocsPath]);

  useEffect(() => {
    fetchHelp();
  }, [fetchHelp]);

  useEffect(() => {
    onShowBackButton(contentHistoryRef.current.length > 1);
  }, [currentContent, onShowBackButton]);

  return (
    currentContent && (
      <div ref={contentContainerRef} className={styles.Container}>
        <MarkdownViewer
          disablePermalink={true}
          markdown={currentContent}
          docsUrl={isPlatformHelp ? platformDocsUrl : 'platform/docs'}
          onLinkClick={handleLinkClick}
          onDraggingKemuSnippet={onDraggingKemuSnippet}
        />
      </div>
    )
  );
});

export default NativeWidgetHelpContent;
